<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrency } from '@composables/useCurrency';
import { trans } from '@elements/translation';
import { currency } from '@elements/number-formatter';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { useDataTableStore } from '@stores/useDataTableStore';
import useInvoiceStore from '../store/invoices';
import Button from '../../../_01_atoms/components/Button.vue';
import useInvoiceModalStore from '../store/invoiceModal';
import Typography from '../../../_01_atoms/components/Typography.vue';
import { INVOICE_STATUS } from '../constants/constants';
import BillingDocumentSummaryCard from './cards/BillingDocumentSummaryCard.vue';

const { currencyField } = useCurrency();

const datatableStore = useDataTableStore();
const invoiceStore = useInvoiceStore();
const { selectedInvoice } = storeToRefs(invoiceStore);

const invoiceAmount = computed(() => currency(selectedInvoice.value.amount / 100) ?? null);

const { details } = storeToRefs(useInvoiceModalStore());
const partialMode = computed(() => details.value?.partialMode ?? false);

const remainingRef = ref(selectedInvoice.value.amount);
const amountRef = ref(selectedInvoice.value.amount);

const remainingAmount = currencyField(remainingRef);
const amount = currencyField(amountRef);

const emit = defineEmits([ 'close' ]);

const creditNoteTranslationKey = (partialMode.value)
  ? 'gestion.invoices.slider.partial_credit_note.label.value'
  : 'gestion.invoices.slider.credit_note.label.value';

const creditNoteLabel = computed(() => trans(creditNoteTranslationKey, { credit_note: selectedInvoice.value.invoice_number }));

const doCreateCreditNote = async () => {
  if (amount.value > remainingAmount.value) {
    flashBagError(trans('gestion.credit_note.amount_invalid'));
    return;
  }

  const data = await invoiceStore.createCreditNote(selectedInvoice.value.id, creditNoteLabel.value, amountRef.value);

  if (!data?.credit_note) {
    return;
  }

  let status = INVOICE_STATUS.PAID;
  let flashbagMessage = trans('gestion.invoices.slider.credit_note.flashbag.success');

  if (partialMode.value && remainingAmount.value > amount.value) {
    status = INVOICE_STATUS.PARTIALLY_PAID;
    flashbagMessage = trans('gestion.invoices.slider.partial_credit_note.flashbag.success');
  }

  selectedInvoice.value.status = status;

  datatableStore.addData(data.credit_note);
  datatableStore.updateDataById(selectedInvoice.value.row_id, selectedInvoice.value);

  flashBagSuccess(flashbagMessage);
  emit('close');
};

onMounted(async () => {
  if (!partialMode.value) {
    return;
  }

  remainingRef.value = await invoiceStore.getMaxRefundableAmount(selectedInvoice.value.id);
  amountRef.value = null;
});
</script>

<template>
  <div class="slider-credit-note">
    <div class="slider-credit-note__content">
      <BillingDocumentSummaryCard :invoice="selectedInvoice" />
      <form class="slider-credit-note__form">
        <div>
          <label for="credit-note-label">{{ trans('gestion.invoices.slider.credit_note.label') }}</label>
          <input
            id="credit-note-label"
            type="text"
            name="label"
            disabled
            :value="creditNoteLabel"
          >
        </div>
        <div>
          <template v-if="partialMode">
            <label for="amount">
              {{ trans('gestion.invoices.slider.credit_note.amount') }}
              <Typography as="span" variant="body" class="text-align-right">
                {{ trans('gestion.invoices.slider.credit_note.remaining', { remaining: currency(remainingAmount) }) }}
              </Typography>
            </label>
            <input
              id="amount"
              v-model="amount"
              type="number"
              name="amount"
              :max="remainingAmount"
            >
          </template>
          <template v-else>
            <label for="amount">{{ trans('gestion.invoices.slider.credit_note.amount') }}</label>
            <input
              id="amount"
              type="text"
              name="amount"
              disabled
              :value="invoiceAmount"
            >
          </template>
        </div>
      </form>
    </div>
    <div class="slider-credit-note__footer">
      <div class="slider-credit-note__footer-buttons">
        <Button variant="secondary" @click="emit('close')">
          {{ trans('gestion.invoices.slider.credit_note.ctas.cancel') }}
        </Button>
        <Button
          variant="primary"
          :class="{ 'loading-btn': invoiceStore.isLoading() }"
          :disabled="invoiceStore.isLoading()"
          @click="doCreateCreditNote"
        >
          {{
            partialMode
              ? trans('gestion.invoices.slider.partial_credit_note.ctas.confirm')
              : trans('gestion.invoices.slider.credit_note.ctas.confirm')
          }}
        </Button>
      </div>
    </div>
  </div>
</template>
